import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { startEmbeddedSignup } from "../store/whatsappSignupSlice";
import { showAlert } from "../store/alertSlice";

const SDK_CONFIG = {
    SRC: 'https://connect.facebook.net/en_US/sdk.js',
    APP_ID: process.env.REACT_APP_FACEBOOK_SDK_APP_ID,
    SIGNUP_CONFIG_ID: process.env.REACT_APP_FACEBOOK_SIGNUP_CONFIG_ID,
    VERSION: 'v24.0'
};

export const useWhatsAppSDK = (onSuccess) => {
    const dispatch = useDispatch();
    const [sdkLoaded, setSdkLoaded] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);

    const codeRef = useRef(null);
    const embeddedSignupEventRef = useRef(null);

    // Initialize Facebook SDK
    useEffect(() => {
        if (window.FB) {
            setSdkLoaded(true);
            return;
        }

        const existing = document.querySelector(`script[src="${SDK_CONFIG.SRC}"]`);
        if (!existing) {
            const script = document.createElement('script');
            script.src = SDK_CONFIG.SRC;
            script.async = true;
            script.defer = true;
            script.crossOrigin = 'anonymous';
            document.body.appendChild(script);

            script.onload = () => setSdkLoaded(true);
        } else {
            setSdkLoaded(true);
        }

        window.fbAsyncInit = function () {
            if (!window.FB) return;
            window.FB.init({
                appId: SDK_CONFIG.APP_ID,
                autoLogAppEvents: true,
                xfbml: true,
                version: SDK_CONFIG.VERSION
            });
        };

        // Message listener for embedded signup response
        const handleMessage = (event) => {
            if (!event.origin || !event.origin.endsWith('facebook.com')) return;

            let data = event.data;
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch (e) {
                    return;
                }
            }

            if (data?.type === 'WA_EMBEDDED_SIGNUP') {
                const payloadToStore = data.payload || data;
                embeddedSignupEventRef.current = payloadToStore;

                if (codeRef.current) {
                    payloadToStore.code = codeRef.current;
                    dispatch(startEmbeddedSignup(payloadToStore));
                    codeRef.current = null;
                    embeddedSignupEventRef.current = null;

                    setIsConnecting(false);
                    if (onSuccess) {
                        onSuccess();
                    }
                }
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [dispatch, onSuccess]);

    // Facebook login callback
    const fbLoginCallback = useCallback((response) => {
        if (response?.authResponse?.code) {
            const code = response.authResponse.code;
            codeRef.current = code;

            if (embeddedSignupEventRef.current) {
                const payload = embeddedSignupEventRef.current;
                payload.code = codeRef.current;
                dispatch(startEmbeddedSignup(payload));
                codeRef.current = null;
                embeddedSignupEventRef.current = null;
                
                setIsConnecting(false);
                if (onSuccess) {
                    onSuccess();
                }
            }
        } else {
            dispatch(showAlert({ 
                message: 'Facebook login cancelled or failed', 
                type: 'error' 
            }));
            setIsConnecting(false);
        }
    }, [dispatch, onSuccess]);

    // Handle connection initiation
    const handleConnect = useCallback(() => {
        if (!window.FB || typeof window.FB.login !== 'function') {
            dispatch(showAlert({ 
                message: 'Facebook SDK not ready yet. Please wait a moment and try again.', 
                type: 'error' 
            }));
            return;
        }

        setIsConnecting(true);

        // Launch Facebook embedded signup
        window.FB.login(fbLoginCallback, {
            config_id: SDK_CONFIG.SIGNUP_CONFIG_ID,
            response_type: 'code',
            override_default_response_type: true,
            extras: {
                featureType: "whatsapp_business_app_onboarding",
                sessionInfoVersion: "3",
                features: [
                    {
                        name: "marketing_messages_lite"
                    }
                ],
                version: "v3",
                setup: {}
            }});
    }, [dispatch, fbLoginCallback]);

    return {
        sdkLoaded,
        isConnecting,
        handleConnect
    };
};

export default useWhatsAppSDK;
